import React, {useEffect, useMemo, useState} from "react";
import {Button, EmptyState, Icon, SectionHeading, Table, TableData} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {ArrowTopRightOnSquareIcon, InformationCircleIcon, ShoppingCartIcon} from "@heroicons/react/24/outline";
import {
    CartInTimeline,
    CartKind,
    durationMinutes,
    formatTime,
    isActiveKind
} from "@widgets/reports/cartTimeline/CartTimelineUtils";
import CartKindBadge from "@widgets/reports/cartTimeline/status/CartKindBadge";
import {orderSummary} from "@widgets/reports/cartTimeline/order/CartOrderUtils";
import {posOrders} from "@/routing/Router";

/** A row of the table: the shown values computed up front. */
type CartRow = {
    id: number,
    kind: CartKind,
    start: string,
    end: string,
    duration: string,
    order: string | null,
    fulfillment: string,
    customer: string,
    publicId: string,
    item: CartInTimeline,
};

type Props = {
    carts: CartInTimeline[],
    onSelect: (item: CartInTimeline) => void,
};

/**
 * All (filtered) carts of the day, newest first. The details button opens the
 * same modal as a bar in the timeline.
 */
const CartsTable = ({carts, onSelect}: Props) => {
    const intl = useIntl();
    const rows: CartRow[] = useMemo(
        () => [...carts]
            .sort((a, b) => b.start.getTime() - a.start.getTime() || b.cart.id - a.cart.id)
            .map((item: CartInTimeline): CartRow => {
                const {cart, kind, start, end} = item;
                const minutes: number = durationMinutes(start, end);
                return {
                    id: cart.id,
                    kind,
                    start: formatTime(start),
                    end: kind === "pos"
                        ? "–"
                        : intl.formatMessage({id: `cartTimeline.end.${kind}`}, {
                            time: formatTime(isActiveKind(kind) ? cart.expiresOn : end),
                        }),
                    duration: kind === "pos"
                        ? "–"
                        : intl.formatMessage(
                            {id: isActiveKind(kind) ? "cartTimeline.duration.running" : "cartTimeline.duration"},
                            {minutes},
                        ),
                    order: orderSummary(item),
                    publicId: item.cart.publicId,
                    fulfillment: intl.formatMessage({id: cart.isDelivery ? "delivery" : "takeaway"}),
                    customer: kind === "pos"
                        ? intl.formatMessage({id: "cartTimeline.customer.pos"})
                        : cart.userId === null
                            ? intl.formatMessage({id: "cartTimeline.customer.guest"})
                            : intl.formatMessage({id: "cartTimeline.customer.account"}, {id: cart.userId}),
                    item,
                };
            }),
        [carts, intl]);
    const [data, setData] = useState<TableData<CartRow>[]>(rows);

    // Keep the table in sync when the carts are refetched or filtered
    useEffect(() => setData(rows), [rows]);

    return (
        <div className={"flex flex-col gap-4"}>
            <SectionHeading
                title={<FormattedMessage id={"cartTimeline.allCarts"}/>}
                description={intl.formatMessage({id: "cartTimeline.allCarts.description"}, {n: rows.length})}
            />

            {
                rows.length === 0
                    ? (
                        <EmptyState
                            containerClassname={"py-6"}
                            icon={<Icon icon={ShoppingCartIcon}/>}
                            title={<FormattedMessage id={"cartTimeline.noCarts"}/>}
                            description={<FormattedMessage id={"cartTimeline.noCarts.description"}/>}
                        />
                    )
                    : (
                        <Table<CartRow>
                            columns={[
                                {
                                    id: "id",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.cart"})},
                                    primary: true,
                                    formatter: id => `#${id}`,
                                },
                                {
                                    id: "start",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.start"})},
                                    primary: false,
                                },
                                {
                                    id: "end",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.end"})},
                                    primary: false,
                                },
                                {
                                    id: "duration",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.duration"})},
                                    primary: false,
                                },
                                {
                                    id: "fulfillment",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.fulfillment"})},
                                    primary: false,
                                },
                                {
                                    id: "customer",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.customer"})},
                                    primary: false,
                                },
                                {
                                    id: "order",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.order"})},
                                    align: "right",
                                    primary: false,
                                    formatter: (order: string | null, row: CartRow) => (
                                        order === null
                                            ? ""
                                            : <div className={"inline-flex"}>
                                                <Button
                                                    color={"primary"}
                                                    variant={"link"}
                                                    iconAfter={<Icon icon={ArrowTopRightOnSquareIcon} className={"!size-4"}/>}
                                                    buttonAction={{
                                                        as: "reactRouterLink",
                                                        actionProps: {
                                                            to: posOrders(row.publicId).absolutePath(),
                                                            target: "_blank",
                                                        },
                                                    }}
                                                >
                                                    {order}
                                                </Button>
                                            </div>
                                    ),
                                },
                                {
                                    id: "kind",
                                    label: {displayName: intl.formatMessage({id: "status"})},
                                    align: "right",
                                    formatter: (kind: CartKind) => <CartKindBadge kind={kind}/>,
                                },
                                {
                                    id: "publicId",
                                    label: {displayName: intl.formatMessage({id: "cartTimeline.column.cart"})},
                                    align: "right",
                                    primary: false,
                                    formatter: (_, row: CartRow) => (
                                        <div className={"inline-flex"}>
                                            <Button
                                                color={"secondary"}
                                                variant={"solid"}
                                                iconBefore={<Icon icon={InformationCircleIcon} className={"!size-4"}/>}
                                                buttonAction={{
                                                    as: "button",
                                                    actionProps: {
                                                        onClick: () => onSelect(row.item),
                                                    },
                                                }}
                                            >
                                                <FormattedMessage id={"detailsAlt"}/>
                                            </Button>
                                        </div>
                                    ),
                                },
                            ]}
                            data={data}
                            setData={setData}
                            rowConfig={{
                                hoverEffect: true,
                            }}
                            size={"compact"}
                            breakpoints={{
                                md: {
                                    size: "normal",
                                },
                            }}
                        />
                    )
            }
        </div>
    );
};

export default CartsTable;